import './styles/Card.scss';

import { CaretDown } from '@phosphor-icons/react';
import { JSX, PropsWithChildren, ReactNode, useState } from 'react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks/useClassHelper';
import { colors } from '../../styles/variables';

const className = 'p-Card';

const getMinimisedClasses = (maximised: boolean, headerClasses?: string) => {
  return cn(
    `${className}__card-header`,
    !maximised && `${className}__card-header-closed`,
    headerClasses,
  );
};

interface Props extends PropsWithChildren {
  title?: string | ReactNode | JSX.Element;
  statusComponent?: ReactNode;
  solidCard?: boolean;
  transparent?: boolean;
  noPadding?: boolean;
  paddingLeft?: boolean;
  paddingRight?: boolean;
  boxShadow?: boolean;
  noOverflow?: boolean;
  id?: string;
  fullHeight?: boolean;
  classNames?: {
    header?: string;
    container?: string;
    content?: string;
  };
}

export const Card = (props: Props) => {
  const c = useClassHelper(className);

  const {
    id,
    title,
    statusComponent,
    children,
    solidCard = true,
    transparent = false,
    paddingLeft = true,
    paddingRight = true,
    boxShadow = true,
    noPadding = false,
    noOverflow = false,
    fullHeight = false,
    classNames = {
      header: '',
      container: '',
      content: '',
    },
  } = props;

  const [maximised, setmaximised] = useState(true);

  const titleId = `${id}-title`;
  const descId = `${id}-desc`;

  const mainClasses = cn(
    !paddingLeft && c('--no-padding-left'),
    transparent && c('--transparent'),
    noPadding && c('--no-padding'),
    noOverflow && c('--no-overflow'),
    !paddingRight && c('--no-padding-right'),
    fullHeight && c('--full-height'),
  );

  const hasHeader = title || statusComponent || !solidCard;

  // The card outline is Tailwind, not SCSS. `shadow-sm` matches the old
  // `--box-shadow-default` token exactly; the border is new and deliberate, so
  // it stays on even when the shadow is suppressed. A transparent card has no
  // surface to lift, so it gets neither.
  const surfaceClassName = cn(
    'border border-gray-200',
    boxShadow && !transparent && 'shadow-sm',
  );

  const containerClassName = cn(
    className,
    surfaceClassName,
    mainClasses,
    classNames.container,
  );

  const contentClassName = cn(c('__content'), classNames.content);

  return (
    <article
      className={containerClassName}
      aria-labelledby={title && id ? titleId : undefined}
      aria-describedby={id ? descId : undefined}
    >
      {hasHeader && (
        <header className={getMinimisedClasses(maximised, classNames.header)}>
          {typeof title === 'string' ? (
            <h2 id={titleId} className={c('__card-title')}>
              {title}
            </h2>
          ) : (
            title
          )}
          {statusComponent && statusComponent}
          {!solidCard && (
            <button
              type="button"
              className={c('__icon', {
                '--open': maximised,
                '--closed': !maximised,
              })}
              onClick={() => setmaximised(!maximised)}
            >
              <CaretDown size={24} color={colors.grey900} />
            </button>
          )}
        </header>
      )}

      {maximised && (
        <div className={contentClassName} id={id ? descId : undefined}>
          {children}
        </div>
      )}
    </article>
  );
};
